import { useCallback, useEffect, useState } from 'react';
import type { Observable } from 'rxjs';
import type { HttpError } from 'utils/http/httpClient.model';

export type FetchStatus = 'loading' | 'error' | 'unauthorized' | 'empty' | 'success';

function isForbidden(httpErrorCode: number) {
  return httpErrorCode === 401 || httpErrorCode === 403;
}

export function useFetchArrayData<T>(
  api: () => Observable<ReadonlyArray<T>>,
): [status: FetchStatus, data: ReadonlyArray<T> | null, retry: Nullable<() => void>] {
  return useFetchData(api, d => d.length === 0);
}

export function useFetchData<T>(
  api: () => Observable<T>,
  isEmpty: (data: Readonly<T>) => boolean,
): [status: FetchStatus, data: Readonly<T> | null, retry: Nullable<() => void>] {
  const [data, setData] = useState<Readonly<T>>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [httpErrorCode, setHttpErrorCode] = useState<number | null>();
  const [fetchDataRetries, setFetchDataRetries] = useState<number>(0);

  const handleRetry = useCallback(() => {
    setFetchDataRetries(fetchDataRetries + 1);
  }, [fetchDataRetries]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to rerun this effect every time fetchDataRetries is updated
  useEffect(() => {
    setIsLoading(true);
    const subscription = api().subscribe({
      next(data) {
        setIsLoading(false);
        setHttpErrorCode(null);
        setData(data);
      },
      error({ status }: HttpError) {
        setHttpErrorCode(status);
        setIsLoading(false);
      },
    });
    return function cleanup() {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    };
  }, [api, fetchDataRetries]);

  if (isLoading) {
    return ['loading', null, null];
  }
  if (httpErrorCode) {
    return isForbidden(httpErrorCode) ? ['unauthorized', null, null] : ['error', null, handleRetry];
  }
  if (data === undefined || isEmpty(data)) {
    return ['empty', null, handleRetry];
  }
  return ['success', data!, null];
}
